// The Apps catalog's filter bar — a name search plus country and capability facets,
// each a multi-select drop-down (`core/filter-select.ts`), the same shape the flows
// registry uses.
//
// Filtering is client-side off each row's stamped `data-*` attributes: the rows carry
// unsaved edits (a toggled capability, a changed country), so re-rendering the
// table on every keystroke would throw them away.
import { esc } from '../ui/modal';
import { t } from '../ui/lang';
import { filterSelect, pickFilterSelect, wireFilterSelects } from '../core/filter-select';
import { farmLocation } from '../core/farm-locations';
export function createAppFilterState() {
    return { name: '', countries: new Set(), caps: new Set() };
}
/** Stamp what {@link applyAppFilter} reads onto one rendered app row. */
export function stampAppFilterData(el, a) {
    const ds = el.dataset;
    ds['fname'] = a.name.toLowerCase();
    ds['country'] = a.country ?? '';
    ds['caps'] = [a.supports_deposit ? 'deposit' : '', a.supports_payout ? 'payout' : ''].filter(Boolean).join(',');
}
function rowMatches(state, ds) {
    if (state.name && !(ds['fname'] ?? '').includes(state.name))
        return false;
    if (state.countries.size && !state.countries.has(ds['country'] ?? ''))
        return false;
    // Capabilities are a union: "Deposit + Payout" asks for apps doing either, not both.
    if (state.caps.size) {
        const caps = (ds['caps'] ?? '').split(',');
        if (![...state.caps].some((c) => caps.includes(c)))
            return false;
    }
    return true;
}
/** Show/hide the already-rendered rows per the current selection. Returns what is left. */
export function applyAppFilter(list, state) {
    let shown = 0;
    list.querySelectorAll('tr[data-name]').forEach((row) => {
        const ok = rowMatches(state, row.dataset);
        row.style.display = ok ? '' : 'none';
        if (ok)
            shown += 1;
    });
    return shown;
}
/**
 * Render the bar into `el` and wire it. The country options come from the catalog itself —
 * a country nobody has an app in is an option that can only ever empty the list.
 */
export function buildAppFilter(el, state, apps, onChange) {
    const countries = [...new Set(apps.map((a) => a.country ?? ''))]
        .sort((a, b) => farmLocation(a).label.localeCompare(farmLocation(b).label));
    for (const c of [...state.countries])
        if (!countries.includes(c))
            state.countries.delete(c);
    const countryLabel = (code) => {
        const loc = farmLocation(code);
        return loc.flag ? `${loc.flag} ${loc.label}` : loc.label;
    };
    el.innerHTML = `
        <input type="text" class="farm-filter__search" placeholder="${esc(t('clickertab.search_apps'))}" value="${esc(state.name)}" />
        ${filterSelect(t('clickertab.country'), 'country', countries.map((c) => ({ key: c, label: countryLabel(c), on: state.countries.has(c) })))}
        ${filterSelect(t('clickertab.capability'), 'cap', [
        { key: 'deposit', label: t('clickertab.deposit') },
        { key: 'payout', label: t('clickertab.payout') },
    ].map((c) => ({ key: c.key, label: c.label, on: state.caps.has(c.key) })))}
    `;
    const search = el.querySelector('.farm-filter__search');
    search.addEventListener('input', () => { state.name = search.value.trim().toLowerCase(); onChange(); });
    // The bar is rebuilt on every list reload into the SAME element, so the delegated
    // handlers are wired once — a second one would toggle each selection twice.
    if (el.dataset['wired'])
        return;
    el.dataset['wired'] = '1';
    wireFilterSelects(el);
    el.addEventListener('click', (e) => {
        const opt = e.target.closest('.fsel__opt');
        if (!opt || !el.contains(opt))
            return;
        const key = opt.dataset['key'] ?? '';
        const fsel = opt.closest('.fsel');
        const on = pickFilterSelect(opt);
        const put = (set, value) => { if (on)
            set.add(value);
        else
            set.delete(value); };
        if (fsel.dataset['role'] === 'country')
            put(state.countries, key);
        else if (fsel.dataset['role'] === 'cap')
            put(state.caps, key);
        onChange();
    });
}
